/**
 * Welke zendingregel hoort bij welke factuurregel.
 *
 * Op de factuurdetailpagina staat achter het aantal en de prijs een "Origineel: …"
 * wanneer de factuur afwijkt van wat er daadwerkelijk verzonden is. Daarvoor moet elke
 * factuurregel aan de juiste zendingregel gekoppeld worden, en dat is lastiger dan het
 * lijkt.
 *
 * ── Waarom productId niet genoeg is ──
 *
 * Een zending kan producten uit meerdere inkooporders combineren (§3.2 stap 6 van
 * PURCHASE_FLOW_AUDIT.md). Staat hetzelfde product in twee van die orders, dan zitten er
 * ook twee regels voor dat product in de zending — met elk hun eigen aantal en prijs.
 * Een factuurregel draagt alleen `productId`, géén `purchaseOrderId`, dus daarmee valt
 * niet te zeggen bij welke van de twee hij hoort.
 *
 * Hier stond `items.find((item) => item.productId === productId)`: dat geeft altijd de
 * eerste match. Elke factuurregel van zo'n product werd dus vergeleken met de eerste
 * zendingregel ervan, en kreeg het aantal en de prijs van de verkeerde inkooporder te
 * zien.
 *
 * ── Waarop dan wel ──
 *
 * Twee manieren, in deze volgorde.
 *
 * 1. **Op `purchaseOrderId`.** Het supplier portal zet dat sinds oktober 2026 op elke
 *    factuurregel die het uit een zending maakt (`invoices/create` → `createInvoiceForShipment`).
 *    Draagt de regel het veld, dan is de koppeling eenduidig en hoeft er niets afgeleid
 *    te worden.
 *
 * 2. **Op positie**, voor facturen van vóór die wijziging. De n-de regel van een product
 *    op de factuur hoort bij de n-de regel van dat product in de zending. Dat is geen
 *    gok: het portal bouwt de regels met een rechtstreekse `shipment.items.map(...)`, dus
 *    in dezelfde volgorde. In productie gecontroleerd — van de 31 definitieve facturen met
 *    een zending hadden er 29 een regelvolgorde die exact gelijk was aan die van de
 *    zendingregels, en de gevallen waar het misging zaten daar allemaal bij.
 *
 * Een harde garantie is de positie niet, want in het CMS zijn regels met de hand toe te
 * voegen en te verwijderen. Levert geen van beide manieren een regel op, dan geeft dit
 * `null` terug en toont de pagina gewoon niets. Beter geen origineel dan het verkeerde:
 * dát was precies de klacht.
 */

/** De zendingregel die bij de factuurregel op `lineIndex` hoort, of null. */
export const findShipmentItemForLine = (invoiceLines, shipmentItems, lineIndex) => {
  const lines = invoiceLines || [];
  const items = shipmentItems || [];
  const line = lines[lineIndex];
  if (!line?.productId || items.length === 0) return null;

  const sameProduct = items.filter((item) => item?.productId === line.productId);
  if (sameProduct.length === 0) return null;

  // 1. Eenduidig: de factuurregel weet zelf uit welke inkooporder hij komt.
  if (line.purchaseOrderId) {
    return sameProduct.find((item) => item.purchaseOrderId === line.purchaseOrderId) || null;
  }

  // 2. Oudere facturen: het hoeveelste voorkomen van dit product is deze regel?
  let occurrence = 0;
  for (let i = 0; i < lineIndex && i < lines.length; i++) {
    if (lines[i]?.productId === line.productId) occurrence++;
  }
  return sameProduct[occurrence] || null;
};

/** Het oorspronkelijk verzonden aantal voor deze factuurregel, of null. */
export const getOriginalQuantityForLine = (invoiceLines, shipmentItems, lineIndex) => {
  const item = findShipmentItemForLine(invoiceLines, shipmentItems, lineIndex);
  return item?.quantity ?? null;
};

/** De oorspronkelijke stukprijs voor deze factuurregel, of null. */
export const getOriginalUnitPriceForLine = (invoiceLines, shipmentItems, lineIndex) => {
  const item = findShipmentItemForLine(invoiceLines, shipmentItems, lineIndex);
  return item?.unitPrice ?? null;
};
